<template>
  <div>
    <datepicker
        class="DATE-INPUT"
        :class="{ 'INLINE': inline, 'm-r-2': inline }"
        input-class="input ellipsize"
        calendar-class="CALENDAR"
        :format="date => $options.calendarDate(date, { weekday: true, year: false })"
        :disabled-dates="{ customPredictor: disabledDates.customPredictor.bind(undefined, 'date') }"
        :disabled="disabled"
        :value="value"
        @input="onDateInput"
    />
    <select
        v-if="$device.isMobile"
        class="input NATIVE-DATE-INPUT"
        :class="{ 'INLINE': inline }"
        :value="timeOffsetInMinutes"
        :disabled="disabledTime || disabled"
        @input="onTimeInput($event.target.value)">
      <option
          v-for="(timeStep, index) in timeSteps"
          :disabled="disabledDates.customPredictor('time', generateDateTime(index))"
          :key="index"
          :value="index * step">
        {{ generateDateTime(index) | timeOfDay }}
      </option>
    </select>
    <vk-listbox
        v-else
        :value="timeOffsetInMinutes"
        :display-text="$options.timeOfDay(value)"
        :disabled="disabledTime || disabled"
        class="DATE-INPUT"
        :class="{ 'INLINE': inline }"
        menu-class="MENU"
        @input="onTimeInput">
      <vk-listbox-option
          v-for="(timeStep, index) in timeSteps"
          :class="{ 'DISABLED': disabledDates.customPredictor('time', generateDateTime(index)) }"
          :key="index"
          :value="index * step">
        {{ generateDateTime(index) | timeOfDay }}
      </vk-listbox-option>
    </vk-listbox>
  </div>
</template>

<script>
import _noop from 'lodash/noop';
import {
  isBefore,
  isAfter,
  setHours,
  setMinutes,
} from 'date-fns';
import Datepicker from 'vuejs-datepicker';
import {
  calendarDate,
  timeOfDay,
} from '@homeservices/tool-kit/proreferral/utils/time-formats';

const MINUTES_IN_DAY = 1440;
const MINUTES_IN_HOUR = 60;

export default {
  calendarDate,
  timeOfDay,
  isBefore,
  isAfter,
  name: 'date-time-input',
  components: {
    Datepicker,
  },
  model: {
    prop: 'value',
    event: 'change',
  },
  props: {
    value: {
      type: Date,
      required: true,
    },
    disabled: Boolean,
    disabledTime: Boolean,
    disabledDates: {
      type: Object,
      default: () => ({
        customPredictor: _noop,
      }),
    },
    step: {
      type: Number,
      default: 30,
    },
    inline: Boolean,
  },
  computed: {
    timeSteps () {
      return MINUTES_IN_DAY / this.step;
    },
    timeOffsetInMinutes () {
      const hours = this.value.getHours();
      const minutes = this.value.getMinutes();

      return hours * MINUTES_IN_HOUR + Math.floor(minutes / this.step) * this.step;
    },
  },
  watch: {
    disabledTime () {
      this.onDateInput(this.value);
    },
  },
  mounted () {
    this.onChange(
      this.value,
      this.getHoursAndMinutes(this.timeOffsetInMinutes)
    );
  },
  methods: {
    generateDateTime (index) {
      return this.setHoursAndMinutes(this.value, this.getHoursAndMinutes(index * this.step));
    },
    getHoursAndMinutes (timeOffsetInMinutes) {
      return {
        hours: Math.floor(timeOffsetInMinutes / MINUTES_IN_HOUR),
        minutes: timeOffsetInMinutes % MINUTES_IN_HOUR,
      };
    },
    setHoursAndMinutes (date, { hours, minutes }) {
      return setHours(setMinutes(date, minutes), hours);
    },
    onDateInput (date) {
      this.onChange(
        date,
        this.getHoursAndMinutes(this.disabledTime ? 0 : this.timeOffsetInMinutes)
      );
    },
    onTimeInput (timeOffsetInMinutes) {
      this.onChange(
        this.value,
        this.getHoursAndMinutes(timeOffsetInMinutes)
      );
    },
    onChange (date, hoursAndMinutes) {
      this.$emit('change', this.setHoursAndMinutes(date, hoursAndMinutes));
    },
  },
};
</script>

<style lang="scss" scoped>
  @import "~@homeservices/design-kit/tools";

  .DATE-INPUT {
    width: calc(50% - 8px);
    margin-left: $unit;

    &:first-child {
      margin-left: 0;
      margin-right: $unit;
    }

    .DISABLED {
      opacity: 0.3;
      pointer-events: none;
    }

    /deep/ &.INLINE {
      width: auto;
      margin: 0;

      .CALENDAR {
        left: 50%;
        transform: translateX(-50%);
      }

      .MENU {
        min-width: 110px;
        right: 0;
        border: none;
        box-shadow: 0 9px 10px 0 rgba(black, 0.2);
      }

      .input {
        padding: 0;
        color: $color-gray70;
        border: none;
        height: 4*$unit;
        text-align: right;

        .VK-LISTBOX-TEXT {
          padding: 0;
        }
      }
    }

    /deep/ .CHEVRON {
      display: none;
    }
  }

  .NATIVE-DATE-INPUT {
    &.INLINE {
      width: auto;
      margin: 0;
      padding: 0;
      color: $color-gray70;
      border: none;
      height: 4*$unit;
      background: none;
    }
  }
</style>
